import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  parseISO, isToday, isFuture, isSameMonth, getDay, isPast,
} from 'date-fns';
import {
  getAttendance, getAttendanceSummary, markAttendance, markCheckOut,
  getPunches,
  getCorrectionRequests, requestAttendanceCorrection,
  approveAttendanceCorrection, rejectAttendanceCorrection, uploadsUrl,
} from '../api';
import { useAuth } from '../contexts/AuthContext';
import HolidaysTab from './Holidays';
import { exportToExcel } from '../utils/exportExcel';

// ── Types ─────────────────────────────────────────────────────────────────────

type AttendanceStatus = 'PRESENT' | 'WFH' | 'HALF_DAY' | 'ABSENT' | 'ON_LEAVE';

/**
 * The individual in/out taps behind one day.
 *
 * A day row shows first-in and last-out, which is the whole story for office
 * staff who arrive once and leave once. For field staff who break for lunch it
 * is not: the same two timestamps hide a two-hour gap in the middle. This
 * fetches on expand rather than with the month, because most days have nothing
 * extra to say and the request is only worth making for the day being examined.
 */
const PunchBreakdown: React.FC<{ staffId: number; date: string }> = ({ staffId, date }) => {
  const [open, setOpen] = useState(false);
  const [punches, setPunches] = useState<any[] | null>(null);
  const [worked, setWorked] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && punches === null) {
      setLoading(true);
      try {
        const res = await getPunches({ staffId, date: date.slice(0, 10) });
        setPunches(res.data.punches ?? []);
        setWorked(res.data.workedMinutes ?? 0);
      } catch {
        setPunches([]);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="mt-3">
      <button type="button" onClick={toggle} className="text-xs text-blue-600 hover:underline">
        {open ? 'Hide' : 'Show'} in/out detail
      </button>
      {open && (
        <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
          {loading && <p className="text-xs text-gray-500">Loading…</p>}
          {!loading && punches && punches.length > 0 && (
            <>
              <ul className="divide-y divide-gray-200">
                {punches.map((p) => (
                  <li key={p.id} className="flex items-center justify-between py-1.5 text-sm">
                    <span className={p.direction === 'IN' ? 'text-green-700' : 'text-orange-700'}>
                      {p.direction === 'IN' ? 'Checked in' : 'Checked out'}
                    </span>
                    <span className="flex items-center gap-2 text-gray-900">
                      {p.latitude && <span title="Location recorded">📍</span>}
                      {p.photoPath && <span title="Photo captured">📷</span>}
                      <span className="tabular-nums">
                        {new Date(p.punchedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-600 mt-2">
                {punches.length} punches · {Math.floor(worked / 60)} hr {worked % 60} min worked (breaks excluded)
              </p>
            </>
          )}
          {!loading && punches && punches.length === 0 && (
            <p className="text-xs text-gray-500">No individual punches recorded for this day.</p>
          )}
        </div>
      )}
    </div>
  );
};

interface AttendanceRecord {
  id: number; staffId: number; date: string; status: AttendanceStatus; notes?: string;
  photoPath?: string; latitude?: number; longitude?: number; checkedInAt?: string; checkOutAt?: string;
}

interface SummaryRow {
  staff: { id: number; staffName: string };
  present: number; wfh: number; halfDay: number; absent: number; onLeave: number; total: number;
  proofCount: number; missingCheckOutCount: number;
}

interface CorrectionRequest {
  id: number; staffId: number;
  staff: { id: number; staffName: string; reportingPartnerId?: number };
  date: string; requestedStatus: AttendanceStatus; reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reviewedBy?: { id: number; staffName: string };
  reviewedAt?: string; rejectionReason?: string; createdAt: string;
}

interface ProofViewerState {
  record: AttendanceRecord;
  staffName?: string;
  allowCorrection?: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_OPTIONS: AttendanceStatus[] = ['PRESENT', 'WFH', 'HALF_DAY', 'ABSENT', 'ON_LEAVE'];
const STATUS_LABEL: Record<AttendanceStatus, string> = { PRESENT: 'Present', WFH: 'Work From Home', HALF_DAY: 'Half Day', ABSENT: 'Absent', ON_LEAVE: 'On Leave' };
const STATUS_BG: Record<AttendanceStatus, string> = {
  PRESENT: 'bg-green-100 text-green-800 border-green-200',
  WFH: 'bg-blue-100 text-blue-800 border-blue-200',
  HALF_DAY: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  ABSENT: 'bg-red-100 text-red-800 border-red-200',
  ON_LEAVE: 'bg-purple-100 text-purple-800 border-purple-200',
};
const STATUS_DOT: Record<AttendanceStatus, string> = { PRESENT: 'bg-green-500', WFH: 'bg-blue-500', HALF_DAY: 'bg-yellow-400', ABSENT: 'bg-red-500', ON_LEAVE: 'bg-purple-500' };
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const CORR_STATUS_COLOR: Record<string, string> = { PENDING: 'bg-yellow-100 text-yellow-700', APPROVED: 'bg-green-100 text-green-700', REJECTED: 'bg-red-100 text-red-700' };
const TEAM_STATUS_FILTERS: Array<'ALL' | AttendanceStatus> = ['ALL', ...STATUS_OPTIONS];

// ── CheckIn Modal (camera + location) ────────────────────────────────────────

interface CheckInModalProps {
  dateStr: string;
  initialStatus: AttendanceStatus;
  initialNotes: string;
  isAdmin: boolean;
  onDone: () => void;
  onClose: () => void;
}

type CaptureStep = 'form' | 'camera' | 'preview' | 'submitting';

const CheckInModal: React.FC<CheckInModalProps> = ({ dateStr, initialStatus, initialNotes, isAdmin, onDone, onClose }) => {
  const [step, setStep] = useState<CaptureStep>('form');
  const [status, setStatus] = useState<AttendanceStatus>(initialStatus);
  const [notes, setNotes] = useState(initialNotes);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [capturedURL, setCapturedURL] = useState<string | null>(null);
  const [location, setLocation] = useState<GeolocationCoordinates | null>(null);
  const [locError, setLocError] = useState('');
  const [camError, setCamError] = useState('');
  const [error, setError] = useState('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    return () => stopCamera();
  }, []);

  const startCamera = async () => {
    setStep('camera');
    setCamError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      // Request location simultaneously
      setLocError('');
      navigator.geolocation.getCurrentPosition(
        pos => setLocation(pos.coords),
        err => setLocError(`Location unavailable: ${err.message}`),
        { timeout: 15000, enableHighAccuracy: true },
      );
    } catch (err: any) {
      setCamError('Camera access denied. Please allow camera access and try again.');
      setStep('form');
    }
  };

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')!.drawImage(video, 0, 0);
    stopCamera();
    canvas.toBlob(blob => {
      if (!blob) return;
      setCapturedBlob(blob);
      setCapturedURL(URL.createObjectURL(blob));
      setStep('preview');
    }, 'image/jpeg', 0.85);
  };

  const retake = () => {
    if (capturedURL) URL.revokeObjectURL(capturedURL);
    setCapturedBlob(null);
    setCapturedURL(null);
    setLocation(null);
    startCamera();
  };

  const submit = async () => {
    setError('');
    if (!capturedBlob) { setError('Please take a photo first.'); return; }
    if (!location) { setError(locError || 'Waiting for location… please try again.'); return; }
    setStep('submitting');
    try {
      await markAttendance(
        { date: dateStr, status, notes, latitude: location.latitude, longitude: location.longitude, locationAccuracy: location.accuracy },
        capturedBlob,
      );
      onDone();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save attendance.');
      setStep('preview');
    }
  };

  // Admin path — no photo/location required
  const submitAdmin = async () => {
    setError('');
    setStep('submitting');
    try {
      await markAttendance({ date: dateStr, status, notes });
      onDone();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save attendance.');
      setStep('form');
    }
  };

  const mapsUrl = location
    ? `https://www.google.com/maps?q=${location.latitude},${location.longitude}`
    : null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Mark Attendance</h4>
            <p className="text-xs text-gray-400">{format(parseISO(dateStr), 'EEEE, dd MMMM yyyy')}</p>
          </div>
          <button onClick={() => { stopCamera(); onClose(); }} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Status + notes always visible */}
          {(step === 'form' || step === 'preview') && (
            <>
              <div>
                <label className="label text-xs">Status</label>
                <select className="input-field" value={status} onChange={e => setStatus(e.target.value as AttendanceStatus)}>
                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
              </div>
              <div>
                <label className="label text-xs">Notes (optional)</label>
                <input className="input-field" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any notes…" />
              </div>
            </>
          )}

          {/* Camera view */}
          {step === 'camera' && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500 text-center">Position your face in frame and click capture</p>
              <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3]">
                <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
              </div>
              {locError && <p className="text-xs text-amber-600 text-center">{locError}</p>}
              {!location && !locError && <p className="text-xs text-gray-400 text-center">📍 Getting location…</p>}
              {location && <p className="text-xs text-green-600 text-center">📍 Location captured (±{Math.round(location.accuracy)}m)</p>}
              <button onClick={capture} className="btn-primary w-full flex items-center justify-center gap-2">
                <span>📸</span> Capture Photo
              </button>
              <button onClick={() => { stopCamera(); setStep('form'); }} className="btn-secondary w-full text-sm">Cancel</button>
            </div>
          )}

          {/* Preview */}
          {step === 'preview' && capturedURL && (
            <div className="space-y-3">
              <div className="rounded-xl overflow-hidden border border-gray-200">
                <img src={capturedURL} alt="Captured selfie" className="w-full object-cover" />
              </div>
              {location ? (
                <a href={mapsUrl!} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-green-700 hover:underline">
                  <span>📍</span> Location verified (±{Math.round(location.accuracy)}m) — view on map
                </a>
              ) : (
                <p className="text-xs text-amber-600">⚠ Location not yet captured. {locError || 'Please wait…'}</p>
              )}
              {error && <p className="text-xs text-red-600">{error}</p>}
              <button onClick={retake} className="btn-secondary w-full text-sm">Retake Photo</button>
            </div>
          )}

          {/* Form step — for admin it goes straight to submit; for staff it opens camera */}
          {step === 'form' && (
            <>
              {camError && <p className="text-xs text-red-600">{camError}</p>}
              {error && <p className="text-xs text-red-600">{error}</p>}
              {isAdmin ? (
                <div className="flex gap-2 pt-1">
                  <button className="btn-secondary flex-1 text-sm" onClick={onClose}>Cancel</button>
                  <button className="btn-primary flex-1 text-sm" onClick={submitAdmin}>Save</button>
                </div>
              ) : (
                <button className="btn-primary w-full flex items-center justify-center gap-2" onClick={startCamera}>
                  <span>📷</span> Take Selfie &amp; Capture Location
                </button>
              )}
            </>
          )}

          {/* Submit button on preview */}
          {step === 'preview' && (
            <button className="btn-primary w-full" onClick={submit} disabled={!location}>
              {!location ? 'Waiting for location…' : 'Submit Attendance'}
            </button>
          )}

          {step === 'submitting' && (
            <div className="text-center py-4 text-gray-500 text-sm">Saving…</div>
          )}
        </div>

        {/* Hidden canvas for capture */}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

const Attendance: React.FC = () => {
  const { user, isAdmin, isHR, isPartner } = useAuth();
  const canManage = isAdmin || isHR;
  const canManageTeam = canManage || isPartner;
  // Admin, IT and Partners are exempt from mandatory proof/check-out. HR is not.
  const isExemptFromProof = isAdmin || isPartner || user?.role === 'IT';

  // Derive today's date in IST (UTC+5:30) to avoid off-by-one at UTC day boundaries
  const getISTToday = () => {
    const now = new Date();
    return new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
  };
  const today = getISTToday();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  const [activeTab, setActiveTab] = useState<'my' | 'team' | 'corrections' | 'holidays'>('my');

  // My attendance
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [loadingMy, setLoadingMy] = useState(true);

  // Team summary
  const [summary, setSummary] = useState<SummaryRow[]>([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [selectedTeamStaff, setSelectedTeamStaff] = useState<SummaryRow['staff'] | null>(null);
  const [teamRecords, setTeamRecords] = useState<AttendanceRecord[]>([]);
  const [loadingTeamDetails, setLoadingTeamDetails] = useState(false);
  const [teamSearch, setTeamSearch] = useState('');
  const [teamStatusFilter, setTeamStatusFilter] = useState<'ALL' | AttendanceStatus>('ALL');

  // Correction requests
  const [corrections, setCorrections] = useState<CorrectionRequest[]>([]);
  const [loadingCorr, setLoadingCorr] = useState(false);

  // Check-in modal (today only)
  const [checkInDate, setCheckInDate] = useState<string | null>(null);
  const [checkInStatus, setCheckInStatus] = useState<AttendanceStatus>('PRESENT');
  const [checkInNotes, setCheckInNotes] = useState('');
  const popoverRef = useRef<HTMLDivElement>(null);

  // Check-out
  const [checkOutLoading, setCheckOutLoading] = useState(false);
  const [checkOutError, setCheckOutError] = useState('');

  // Proof viewer
  const [proofView, setProofView] = useState<ProofViewerState | null>(null);

  // Correction request modal
  const [corrModal, setCorrModal] = useState<{ dateStr: string; currentStatus?: AttendanceStatus } | null>(null);
  const [corrStatus, setCorrStatus] = useState<AttendanceStatus>('PRESENT');
  const [corrReason, setCorrReason] = useState('');
  const [corrSubmitting, setCorrSubmitting] = useState(false);
  const [corrError, setCorrError] = useState('');

  // Reject correction modal
  const [rejectCorrId, setRejectCorrId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const monthParam = `${year}-${String(month).padStart(2, '0')}`;

  // ── Data fetching ─────────────────────────────────────────────────────────

  const fetchMyAttendance = useCallback(async () => {
    setLoadingMy(true);
    try {
      const res = await getAttendance({ month: monthParam });
      setRecords(res.data);
    } finally { setLoadingMy(false); }
  }, [monthParam]);

  const fetchTeamSummary = useCallback(async () => {
    if (!canManageTeam) return;
    setLoadingTeam(true);
    try {
      const res = await getAttendanceSummary({ month: monthParam });
      setSummary(res.data);
    } finally { setLoadingTeam(false); }
  }, [canManageTeam, monthParam]);

  const fetchTeamDetails = useCallback(async () => {
    if (!canManageTeam || !selectedTeamStaff) {
      setTeamRecords([]);
      return;
    }
    setLoadingTeamDetails(true);
    try {
      const res = await getAttendance({ month: monthParam, staffId: selectedTeamStaff.id });
      setTeamRecords(res.data || []);
    } finally {
      setLoadingTeamDetails(false);
    }
  }, [canManageTeam, monthParam, selectedTeamStaff]);

  const fetchCorrections = useCallback(async () => {
    setLoadingCorr(true);
    try {
      const res = await getCorrectionRequests();
      setCorrections(res.data || []);
    } finally { setLoadingCorr(false); }
  }, []);

  useEffect(() => { fetchMyAttendance(); }, [fetchMyAttendance]);
  useEffect(() => { if (activeTab === 'team') fetchTeamSummary(); }, [activeTab, fetchTeamSummary]);
  useEffect(() => {
    if (summary.length === 0) {
      setSelectedTeamStaff(null);
      return;
    }
    if (!selectedTeamStaff || !summary.some((row) => row.staff.id === selectedTeamStaff.id)) {
      setSelectedTeamStaff(summary[0].staff);
    }
  }, [summary, selectedTeamStaff]);
  useEffect(() => { if (activeTab === 'team') fetchTeamDetails(); }, [activeTab, fetchTeamDetails]);
  useEffect(() => { if (activeTab === 'corrections') fetchCorrections(); }, [activeTab, fetchCorrections]);


  // ── Month navigation ──────────────────────────────────────────────────────

  const changeMonth = (delta: number) => {
    let m = month + delta; let y = year;
    if (m > 12) { m = 1; y += 1; }
    if (m < 1) { m = 12; y -= 1; }
    setMonth(m); setYear(y);
  };

  // ── Calendar logic ────────────────────────────────────────────────────────

  const monthStart = startOfMonth(new Date(year, month - 1, 1));
  const monthEnd = endOfMonth(monthStart);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDow = getDay(monthStart);
  const gridOffset = startDow === 0 ? 6 : startDow - 1;

  const recordMap: Record<string, AttendanceRecord> = {};
  records.forEach(r => { recordMap[r.date.slice(0, 10)] = r; });

  const todayStr = today.toISOString().slice(0, 10);

  const openPopover = (dateStr: string) => {
    const existing = recordMap[dateStr];
    // Block staff from re-marking if already checked in (exempt: Admin, HR, Partner, IT)
    if (!isExemptFromProof && existing?.checkedInAt) return;
    setCheckInStatus(existing?.status ?? 'PRESENT');
    setCheckInNotes(existing?.notes ?? '');
    setCheckInDate(dateStr);
  };

  const handleCheckOut = async () => {
    setCheckOutError('');
    setCheckOutLoading(true);
    try {
      await markCheckOut();
      await fetchMyAttendance();
    } catch (err: any) {
      setCheckOutError(err.response?.data?.message || 'Failed to mark check-out.');
    } finally {
      setCheckOutLoading(false);
    }
  };

  const openCorrModal = (dateStr: string) => {
    const existing = recordMap[dateStr];
    setCorrStatus(existing?.status ?? 'PRESENT');
    setCorrReason('');
    setCorrError('');
    setCorrModal({ dateStr, currentStatus: existing?.status });
  };


  const handleCorrSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setCorrError('');
    if (!corrReason.trim()) { setCorrError('Reason is required'); return; }
    setCorrSubmitting(true);
    try {
      await requestAttendanceCorrection({ date: corrModal!.dateStr, requestedStatus: corrStatus, reason: corrReason });
      setCorrModal(null); setCorrReason('');
      fetchCorrections();
    } catch (err: any) {
      setCorrError(err.response?.data?.message || 'Error submitting request');
    } finally { setCorrSubmitting(false); }
  };

  const handleApproveCorr = async (id: number) => {
    try { await approveAttendanceCorrection(id); fetchCorrections(); fetchMyAttendance(); }
    catch (err: any) { alert(err.response?.data?.message || 'Error approving'); }
  };

  const handleRejectCorr = async () => {
    if (!rejectCorrId) return;
    try { await rejectAttendanceCorrection(rejectCorrId, { rejectionReason: rejectReason }); setRejectCorrId(null); setRejectReason(''); fetchCorrections(); }
    catch (err: any) { alert(err.response?.data?.message || 'Error rejecting'); }
  };

  // ── Summary counts ────────────────────────────────────────────────────────

  const counts: Record<AttendanceStatus, number> = { PRESENT: 0, WFH: 0, HALF_DAY: 0, ABSENT: 0, ON_LEAVE: 0 };
  records.forEach(r => { if (r.status in counts) counts[r.status]++; });
  const sundaysInMonth = daysInMonth.filter(d => getDay(d) === 0).length;
  const totalAttended = counts.PRESENT + counts.WFH + counts.HALF_DAY * 0.5 + sundaysInMonth;
  const summaryItems = [
    { label: 'Attended', count: totalAttended, color: 'text-indigo-600' },
    { label: 'Present', count: counts.PRESENT, color: 'text-green-600' },
    { label: 'WFH', count: counts.WFH, color: 'text-blue-600' },
    { label: 'Half Day', count: counts.HALF_DAY, color: 'text-yellow-600' },
    { label: 'Absent', count: counts.ABSENT, color: 'text-red-600' },
    { label: 'On Leave', count: counts.ON_LEAVE, color: 'text-purple-600' },
  ];

  const pendingCorr = corrections.filter(c => c.status === 'PENDING').length;
  const filteredSummary = summary.filter((row) =>
    row.staff.staffName.toLowerCase().includes(teamSearch.trim().toLowerCase())
  );
  const filteredTeamRecords = teamRecords.filter((record) =>
    teamStatusFilter === 'ALL' ? true : record.status === teamStatusFilter
  );
  const selectedTeamSummary = selectedTeamStaff
    ? summary.find((row) => row.staff.id === selectedTeamStaff.id) ?? null
    : null;
  const formatTimeLabel = (value?: string) => (value ? format(new Date(value), 'hh:mm a') : '—');

  const canApproveCorr = (req: CorrectionRequest) => {
    if (canManage) return true;
    if (isPartner && req.staff.reportingPartnerId === user?.staffId) return true;
    return false;
  };

  // ── Export ────────────────────────────────────────────────────────────────

  const handleExportAttendance = () => {
    if (activeTab === 'team') {
      exportToExcel(
        summary.map(s => ({
          'Staff': s.staff.staffName,
          'Attended Days': s.total,
          'Present': s.present,
          'WFH': s.wfh,
          'Half Day': s.halfDay,
          'Absent': s.absent,
          'On Leave': s.onLeave,
          'Missing Check-outs': s.missingCheckOutCount,
        })),
        'Team Attendance',
        `Attendance_Team_${monthParam}`,
      );
    } else {
      // Build a full-month list: Sundays get a synthetic row; all other days use the DB record
      const exportRows = daysInMonth.map(day => {
        const dateStr = format(day, 'yyyy-MM-dd');
        const displayDate = format(day, 'dd-MMM-yy');
        if (getDay(day) === 0) {
          return { 'Date': displayDate, 'Status': 'Sunday', 'Check-in': '', 'Check-out': '', 'Notes': '' };
        }
        const r = recordMap[dateStr];
        if (!r) return null;
        return {
          'Date': displayDate,
          'Status': r.status,
          'Check-in': r.checkedInAt ? format(new Date(r.checkedInAt), 'hh:mm a') : '',
          'Check-out': r.checkOutAt ? format(new Date(r.checkOutAt), 'hh:mm a') : '',
          'Notes': r.notes || '',
        };
      }).filter(Boolean) as Record<string, string>[];
      exportToExcel(
        exportRows,
        'My Attendance',
        `Attendance_${monthParam}`,
      );
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Attendance &amp; Holidays</h2>
          <p className="text-sm text-gray-500 mt-1">Track daily attendance — only today can be marked directly</p>
        </div>
        {activeTab !== 'holidays' && <div className="flex items-center gap-2">
          <button className="btn-secondary px-3 py-1.5 text-sm" onClick={() => changeMonth(-1)}>&#8249;</button>
          <select className="input-field py-1.5 text-sm" value={month} onChange={e => setMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={i + 1}>{format(new Date(year, i, 1), 'MMMM')}</option>
            ))}
          </select>
          <select className="input-field py-1.5 text-sm" value={year} onChange={e => setYear(Number(e.target.value))}>
            {Array.from({ length: 5 }, (_, i) => today.getFullYear() - 2 + i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button className="btn-secondary px-3 py-1.5 text-sm" onClick={() => changeMonth(1)}>&#8250;</button>
          {canManage && (
            <button className="btn-secondary px-3 py-1.5 text-sm" onClick={handleExportAttendance}>
              ⬇ Export Excel
            </button>
          )}
        </div>}
      </div>

      {/* Pending corrections alert */}
      {pendingCorr > 0 && (canManage || isPartner) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 cursor-pointer" onClick={() => setActiveTab('corrections')}>
          ✏️ <strong>{pendingCorr}</strong> attendance correction request{pendingCorr > 1 ? 's' : ''} pending review — <span className="underline">click to review</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 flex-wrap">
        <button onClick={() => setActiveTab('my')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'my' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          My Attendance
        </button>
        {canManageTeam && (
          <button onClick={() => setActiveTab('team')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'team' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            Team Attendance
          </button>
        )}
        <button onClick={() => setActiveTab('corrections')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'corrections' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          ✏️ Corrections{pendingCorr > 0 ? ` (${pendingCorr})` : ''}
        </button>
        <button onClick={() => setActiveTab('holidays')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'holidays' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
          🗓️ Holidays
        </button>
      </div>

      {/* ── HOLIDAYS TAB ── */}
      {activeTab === 'holidays' && <HolidaysTab />}

      {/* ── MY ATTENDANCE ── */}
      {activeTab === 'my' && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {summaryItems.map(s => (
              <div key={s.label} className={`card text-center py-3 ${s.label === 'Attended' ? 'ring-2 ring-indigo-200' : ''}`}>
                <p className={`text-xl font-bold ${s.color}`}>{typeof s.count === 'number' && !Number.isInteger(s.count) ? s.count.toFixed(1) : s.count}</p>
                <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 text-sm text-blue-700">
            📅 <strong>Today only</strong> — You can mark today's attendance directly. Past dates are auto-filled as Absent. To correct a past date, use the <button className="underline font-medium" onClick={() => setActiveTab('corrections')}>Correction Requests</button> tab.
          </div>

          {/* Check-out banner — shown to regular staff who have checked in but not yet checked out today */}
          {!isExemptFromProof && (() => {
            const todayRec = recordMap[todayStr];
            if (!todayRec?.checkedInAt || todayRec.checkOutAt) return null;
            const checkInTime = new Date(todayRec.checkedInAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
            return (
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-semibold text-green-800">You're checked in</p>
                  <p className="text-xs text-green-600">Check-in: {checkInTime} — Mark check-out when you leave</p>
                  {checkOutError && <p className="text-xs text-red-600 mt-1">{checkOutError}</p>}
                </div>
                <button
                  onClick={handleCheckOut}
                  disabled={checkOutLoading}
                  className="btn-secondary text-sm px-4 py-2 border-green-300 text-green-800 hover:bg-green-100 disabled:opacity-50"
                >
                  {checkOutLoading ? 'Saving…' : '🏠 Mark Check-Out'}
                </button>
              </div>
            );
          })()}

          {/* Check-out confirmed banner */}
          {!isExemptFromProof && (() => {
            const todayRec = recordMap[todayStr];
            if (!todayRec?.checkOutAt) return null;
            const inTime = new Date(todayRec.checkedInAt!).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
            const outTime = new Date(todayRec.checkOutAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
            return (
              <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-600">
                ✅ Today: checked in {inTime} — checked out {outTime}
              </div>
            );
          })()}

          {loadingMy ? <p className="text-gray-500 text-sm">Loading...</p> : (
            <div className="card relative">
              <div className="grid grid-cols-7 mb-2">
                {WEEKDAYS.map(d => <div key={d} className="text-center text-xs font-semibold text-gray-400 py-2">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: gridOffset }).map((_, i) => <div key={`e-${i}`} />)}
                {daysInMonth.map(day => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const rec = recordMap[dateStr];
                  const isSunday = getDay(day) === 0;
                  const isInFuture = isFuture(day) && !isToday(day);
                  const isInPast = isPast(day) && !isToday(day);
                  const inMonth = isSameMonth(day, monthStart);

                  const handleClick = () => {
                    if (!inMonth || isInFuture || isSunday) return;
                    if (isToday(day)) {
                      openPopover(dateStr);
                    } else if (isInPast) {
                      if (rec?.photoPath || rec?.latitude) {
                        setProofView({ record: rec, staffName: user?.staffName ?? 'My attendance', allowCorrection: true });
                      } else {
                        openCorrModal(dateStr);
                      }
                    }
                  };

                  if (isSunday) {
                    return (
                      <div key={dateStr}
                        className="relative rounded-lg border border-gray-100 bg-gray-50 p-1.5 min-h-[56px] flex flex-col cursor-default opacity-60"
                        title="Sunday"
                      >
                        <span className="text-xs font-semibold text-gray-400">{format(day, 'd')}</span>
                        <p className="text-center text-[10px] font-medium text-gray-400 mt-auto leading-tight">Sun</p>
                      </div>
                    );
                  }

                  return (
                    <div key={dateStr} onClick={handleClick}
                      className={[
                        'relative rounded-lg border p-1.5 min-h-[56px] flex flex-col transition-shadow',
                        isInFuture ? 'bg-gray-50 border-gray-100 cursor-default opacity-40' :
                          isToday(day) ? 'cursor-pointer hover:shadow-sm ring-2 ring-blue-400' :
                            'cursor-pointer hover:shadow-sm',
                        rec ? `${STATUS_BG[rec.status]} border` : 'bg-white border-gray-200 hover:border-blue-300',
                      ].join(' ')}
                      title={
                        isToday(day)
                          ? (rec?.checkedInAt && !isExemptFromProof ? 'Already checked in' : 'Click to mark today\'s attendance')
                          : isInPast ? 'Click to request correction' : ''
                      }
                    >
                      <span className={`text-xs font-semibold ${isToday(day) ? 'text-blue-600' : 'text-gray-600'}`}>
                        {format(day, 'd')}
                      </span>
                      {isToday(day) && !rec && (
                        <span className="text-[9px] text-blue-500 font-medium leading-tight mt-0.5">Mark</span>
                      )}
                      {isToday(day) && rec && !isExemptFromProof && rec.checkedInAt && (
                        <span className="text-[8px] text-green-600 leading-none">✓ In</span>
                      )}
                      {isInPast && !rec && (
                        <span className="text-[9px] text-red-400 font-medium leading-tight mt-0.5">A</span>
                      )}
                      {isInPast && (
                        <span className="absolute top-0.5 right-0.5 text-[8px] text-gray-400" title="Click to request correction">✏</span>
                      )}
                      {rec && (
                        <div className="mt-auto">
                          <div className={`w-2 h-2 rounded-full mx-auto ${STATUS_DOT[rec.status]}`} />
                          <p className="text-center text-[10px] font-medium leading-tight mt-0.5 truncate">
                            {rec.status === 'HALF_DAY' ? 'Half' : rec.status === 'ON_LEAVE' ? 'Leave' : rec.status === 'WFH' ? 'WFH' : rec.status === 'PRESENT' ? 'P' : 'A'}
                          </p>
                          {rec.photoPath && <p className="text-center text-[8px] text-green-600 leading-none">📷</p>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-gray-100">
                {STATUS_OPTIONS.map(s => (
                  <div key={s} className="flex items-center gap-1.5">
                    <div className={`w-2.5 h-2.5 rounded-full ${STATUS_DOT[s]}`} />
                    <span className="text-xs text-gray-500">{STATUS_LABEL[s]}</span>
                  </div>
                ))}
              </div>

            </div>
          )}
        </div>
      )}

      {/* Check-in modal */}
      {checkInDate && (
        <CheckInModal
          dateStr={checkInDate}
          initialStatus={checkInStatus}
          initialNotes={checkInNotes}
          // Use the proof-exempt flag, not canManage. HR is canManage=true
          // but per policy (see comment near isExemptFromProof) HR must still
          // provide photo + location proof — they aren't exempt. Passing
          // canManage here was hiding the camera path from HR and made
          // attendance marking impossible for them.
          isAdmin={isExemptFromProof}
          onDone={async () => { setCheckInDate(null); await fetchMyAttendance(); }}
          onClose={() => setCheckInDate(null)}
        />
      )}

      {/* ── TEAM OVERVIEW ── */}
      {activeTab === 'team' && canManageTeam && (
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.9fr)]">
          <div className="card space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Team Attendance</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {canManage
                    ? 'Track employee attendance and inspect proof details from one place.'
                    : 'Review direct reportees attendance, check-in proof, and in/out time.'}
                </p>
              </div>
              <input
                className="input-field py-2 text-sm min-w-[220px]"
                value={teamSearch}
                onChange={(e) => setTeamSearch(e.target.value)}
                placeholder="Search staff name..."
              />
            </div>

            {loadingTeam ? <p className="text-gray-500 text-sm">Loading team attendance...</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr>
                    <th className="table-header">Staff Name</th>
                    <th className="table-header text-center text-green-700">Present</th>
                    <th className="table-header text-center text-blue-700">WFH</th>
                    <th className="table-header text-center text-yellow-700">Half Day</th>
                    <th className="table-header text-center text-red-700">Absent</th>
                    <th className="table-header text-center">Proof</th>
                    <th className="table-header text-center">Open</th>
                    <th className="table-header text-center">Attended</th>
                  </tr></thead>
                  <tbody>
                    {filteredSummary.map(row => {
                      const isSelected = selectedTeamStaff?.id === row.staff.id;
                      return (
                        <tr
                          key={row.staff.id}
                          className={`${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'} cursor-pointer`}
                          onClick={() => setSelectedTeamStaff(row.staff)}
                        >
                          <td className="table-cell">
                            <div className="font-medium text-gray-900">{row.staff.staffName}</div>
                            <div className="text-xs text-gray-500 mt-1">Click to inspect records</div>
                          </td>
                          <td className="table-cell text-center font-semibold text-green-700">{row.present}</td>
                          <td className="table-cell text-center font-semibold text-blue-700">{row.wfh}</td>
                          <td className="table-cell text-center font-semibold text-yellow-700">{row.halfDay}</td>
                          <td className="table-cell text-center font-semibold text-red-700">{row.absent}</td>
                          <td className="table-cell text-center font-semibold text-gray-700">{row.proofCount}</td>
                          <td className="table-cell text-center font-semibold text-amber-700">{row.missingCheckOutCount}</td>
                          <td className="table-cell text-center font-semibold text-gray-700">{Number.isInteger(row.total) ? row.total : row.total.toFixed(1)}</td>
                        </tr>
                      );
                    })}
                    {filteredSummary.length === 0 && (
                      <tr><td colSpan={8} className="table-cell text-center text-gray-400 py-10">
                        {summary.length === 0
                          ? `No team attendance found for ${format(new Date(year, month - 1, 1), 'MMMM yyyy')}`
                          : 'No staff matched your search'}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card space-y-4">
            {!selectedTeamStaff ? (
              <div className="py-10 text-center text-sm text-gray-400">
                Pick a staff member to inspect photo proof, map location, and in/out time.
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{selectedTeamStaff.staffName}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {format(new Date(year, month - 1, 1), 'MMMM yyyy')} attendance details
                    </p>
                  </div>
                  <select
                    className="input-field py-2 text-sm min-w-[170px]"
                    value={teamStatusFilter}
                    onChange={(e) => setTeamStatusFilter(e.target.value as 'ALL' | AttendanceStatus)}
                  >
                    {TEAM_STATUS_FILTERS.map((status) => (
                      <option key={status} value={status}>
                        {status === 'ALL' ? 'All statuses' : STATUS_LABEL[status]}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedTeamSummary && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3">
                      <p className="text-xs text-gray-500">Present</p>
                      <p className="text-lg font-semibold text-green-700">{selectedTeamSummary.present}</p>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3">
                      <p className="text-xs text-gray-500">Proof Days</p>
                      <p className="text-lg font-semibold text-gray-900">{selectedTeamSummary.proofCount}</p>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3">
                      <p className="text-xs text-gray-500">Missing Check-Out</p>
                      <p className="text-lg font-semibold text-amber-700">{selectedTeamSummary.missingCheckOutCount}</p>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3">
                      <p className="text-xs text-gray-500">Attended Days</p>
                      <p className="text-lg font-semibold text-gray-900">{Number.isInteger(selectedTeamSummary.total) ? selectedTeamSummary.total : selectedTeamSummary.total.toFixed(1)}</p>
                    </div>
                  </div>
                )}

                {loadingTeamDetails ? <p className="text-gray-500 text-sm">Loading attendance details...</p> : (
                  <div className="space-y-3 max-h-[32rem] overflow-y-auto pr-1">
                    {filteredTeamRecords.map((record) => {
                      const hasProof = Boolean(record.photoPath || (record.latitude != null && record.longitude != null));
                      const hasTiming = Boolean(record.checkedInAt || record.checkOutAt);
                      return (
                        <div key={`${record.staffId}-${record.date}`} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div>
                              <p className="text-sm font-semibold text-gray-900">{format(parseISO(record.date.slice(0, 10)), 'dd-MMM-yy')}</p>
                              <div className="flex items-center gap-2 mt-2 flex-wrap">
                                <span className={`text-xs px-2 py-1 rounded-full font-medium border ${STATUS_BG[record.status]}`}>
                                  {STATUS_LABEL[record.status]}
                                </span>
                                {hasProof && <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Proof captured</span>}
                                {record.checkedInAt && !record.checkOutAt && <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Open session</span>}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="btn-secondary text-sm px-3 py-1.5"
                              onClick={() => setProofView({ record, staffName: selectedTeamStaff.staffName, allowCorrection: false })}
                            >
                              View proof
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                            <div className="rounded-xl bg-gray-50 border border-gray-200 px-3 py-2">
                              <p className="text-xs text-gray-500">Check-In</p>
                              <p className="font-medium text-gray-900">{formatTimeLabel(record.checkedInAt)}</p>
                            </div>
                            <div className="rounded-xl bg-gray-50 border border-gray-200 px-3 py-2">
                              <p className="text-xs text-gray-500">Check-Out</p>
                              <p className="font-medium text-gray-900">{formatTimeLabel(record.checkOutAt)}</p>
                            </div>
                          </div>
                          <PunchBreakdown staffId={record.staffId} date={record.date} />
                          {record.notes && <p className="text-sm text-gray-600 mt-3">{record.notes}</p>}
                          {!hasProof && !hasTiming && !record.notes && (
                            <p className="text-xs text-gray-400 mt-3">No proof or timing details recorded for this day.</p>
                          )}
                        </div>
                      );
                    })}
                    {filteredTeamRecords.length === 0 && (
                      <div className="rounded-2xl border border-dashed border-gray-300 px-4 py-10 text-center text-sm text-gray-400">
                        {teamRecords.length === 0
                          ? 'No attendance records available for this staff member in the selected month.'
                          : 'No attendance records matched the selected status filter.'}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── CORRECTION REQUESTS ── */}
      {activeTab === 'corrections' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 text-sm text-blue-700">
            ℹ️ Attendance correction requests are reviewed by your <strong>Reporting Partner</strong> or HR/Admin. Once approved, your attendance record is updated automatically.
          </div>
          <div className="card overflow-x-auto">
            {loadingCorr ? <p className="text-gray-500 text-sm py-4 text-center">Loading...</p> : (
              <table className="w-full text-sm">
                <thead><tr>
                  {(canManage || isPartner) && <th className="table-header">Staff</th>}
                  <th className="table-header">Date</th>
                  <th className="table-header">Current</th>
                  <th className="table-header">Requested</th>
                  <th className="table-header">Reason</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Reviewed By</th>
                  <th className="table-header">Actions</th>
                </tr></thead>
                <tbody>
                  {corrections.map(c => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      {(canManage || isPartner) && <td className="table-cell font-medium">{c.staff.staffName}</td>}
                      <td className="table-cell text-xs font-mono">{format(new Date(c.date), 'dd-MMM-yy')}</td>
                      <td className="table-cell">
                        {(() => { const rec = recordMap[c.date.slice(0, 10)]; return rec ? <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${STATUS_BG[rec.status]}`}>{STATUS_LABEL[rec.status]}</span> : <span className="text-xs text-gray-400">—</span>; })()}
                      </td>
                      <td className="table-cell">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${STATUS_BG[c.requestedStatus]}`}>{STATUS_LABEL[c.requestedStatus]}</span>
                      </td>
                      <td className="table-cell max-w-[160px]"><span className="text-xs text-gray-600 line-clamp-2">{c.reason}</span></td>
                      <td className="table-cell">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${CORR_STATUS_COLOR[c.status]}`}>{c.status}</span>
                        {c.status === 'REJECTED' && c.rejectionReason && (
                          <p className="text-xs text-red-500 mt-0.5 truncate max-w-[100px]" title={c.rejectionReason}>{c.rejectionReason}</p>
                        )}
                      </td>
                      <td className="table-cell text-xs text-gray-500">
                        {c.reviewedBy ? <><div>{c.reviewedBy.staffName}</div><div className="text-gray-400">{format(new Date(c.reviewedAt!), 'dd-MMM-yy')}</div></> : '—'}
                      </td>
                      <td className="table-cell">
                        {c.status === 'PENDING' && canApproveCorr(c) && (
                          <div className="flex gap-2">
                            <button className="text-green-600 hover:text-green-800 text-xs font-medium" onClick={() => handleApproveCorr(c.id)}>Approve</button>
                            <button className="text-red-600 hover:text-red-800 text-xs font-medium" onClick={() => { setRejectCorrId(c.id); setRejectReason(''); }}>Reject</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {corrections.length === 0 && (
                    <tr><td colSpan={canManage || isPartner ? 8 : 7} className="table-cell text-center text-gray-400 py-10">No correction requests yet</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── Correction Request Modal ── */}
      {corrModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-1">✏️ Request Attendance Correction</h3>
            <p className="text-xs text-gray-500 mb-4">{format(parseISO(corrModal.dateStr), 'EEEE, dd MMMM yyyy')}</p>
            <form onSubmit={handleCorrSubmit} className="space-y-4">
              <div>
                <label className="label text-xs">Correct Status To</label>
                <select className="input-field" value={corrStatus} onChange={e => setCorrStatus(e.target.value as AttendanceStatus)}>
                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
              </div>
              <div>
                <label className="label text-xs">Reason for Correction *</label>
                <textarea className="input-field" rows={3} value={corrReason} onChange={e => setCorrReason(e.target.value)}
                  placeholder="Explain why the correction is needed..." required />
              </div>
              {corrError && <p className="text-red-600 text-sm">{corrError}</p>}
              <div className="flex gap-3 justify-end">
                <button type="button" className="btn-secondary" onClick={() => setCorrModal(null)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={corrSubmitting}>{corrSubmitting ? 'Submitting...' : 'Submit Request'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Reject Correction Modal ── */}
      {rejectCorrId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Reject Correction Request</h3>
            <label className="label">Rejection Reason (optional)</label>
            <textarea className="input-field" rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason for rejection..." />
            <div className="flex gap-3 justify-end mt-4">
              <button className="btn-secondary" onClick={() => setRejectCorrId(null)}>Cancel</button>
              <button className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 font-medium" onClick={handleRejectCorr}>Confirm Reject</button>
            </div>
          </div>
        </div>
      )}

      {/* Proof viewer modal */}
      {proofView && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <h4 className="text-sm font-semibold text-gray-900">{proofView.staffName || 'Attendance'} Proof</h4>
                <p className="text-xs text-gray-400">{format(parseISO(proofView.record.date.slice(0, 10)), 'EEEE, dd MMMM yyyy')}</p>
              </div>
              <button onClick={() => setProofView(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="p-5 space-y-4">
              {proofView.record.photoPath && (
                <img
                  src={uploadsUrl(proofView.record.photoPath)}
                  alt="Check-in selfie"
                  className="w-full rounded-xl border border-gray-200"
                />
              )}
              {proofView.record.latitude != null && proofView.record.longitude != null && (
                <a
                  href={`https://www.google.com/maps?q=${proofView.record.latitude},${proofView.record.longitude}`}
                  target="_blank" rel="noreferrer"
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                >
                  📍 View location on Google Maps
                </a>
              )}
              {(proofView.record.checkedInAt || proofView.record.checkOutAt) && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-gray-50 border border-gray-200 px-3 py-3">
                    <p className="text-xs text-gray-500">Check-In</p>
                    <p className="text-sm font-semibold text-gray-900">{formatTimeLabel(proofView.record.checkedInAt)}</p>
                  </div>
                  <div className="rounded-xl bg-gray-50 border border-gray-200 px-3 py-3">
                    <p className="text-xs text-gray-500">Check-Out</p>
                    <p className="text-sm font-semibold text-gray-900">{formatTimeLabel(proofView.record.checkOutAt)}</p>
                  </div>
                </div>
              )}
              {proofView.record.notes && (
                <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-3">
                  <p className="text-xs text-gray-500">Notes</p>
                  <p className="text-sm text-gray-700 mt-1">{proofView.record.notes}</p>
                </div>
              )}
              {!proofView.record.photoPath && proofView.record.latitude == null && !proofView.record.checkedInAt && !proofView.record.checkOutAt && (
                <p className="text-sm text-gray-400 text-center py-4">No proof captured for this record.</p>
              )}
              {proofView.allowCorrection && (
              <button
                className="btn-secondary w-full text-sm"
                onClick={() => { setProofView(null); openCorrModal(proofView.record.date.slice(0, 10)); }}
              >
                ✏️ Request Correction
              </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;
